/**
 * Tenant & API Server Configuration
 * 
 * Manages dynamic server connection URLs, allowing users to connect to:
 * - Local development: http://localhost:3000/analytics
 * - Dedicated customer subdomains: cust1.eaconnect.io -> https://cust1.eaconnect.io/analytics
 * - Central multi-tenant cloud: app.eaconnect.io -> https://app.eaconnect.io/analytics
 */

export const TENANT_STORAGE_KEY = 'eaconnect_api_base_url';

export const DEFAULT_API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/analytics';

/**
 * Normalizes user-entered workspace/server input into a valid HTTPS (or localhost HTTP) base URL.
 * Examples:
 * - "cust1" -> "https://cust1.eaconnect.io/analytics"
 * - "cust1.eaconnect.io" -> "https://cust1.eaconnect.io/analytics"
 * - "https://cust1.eaconnect.io" -> "https://cust1.eaconnect.io/analytics"
 * - "localhost:3000" -> "http://localhost:3000/analytics"
 * - "app.eaconnect.io" -> "https://app.eaconnect.io/analytics"
 */
export function normalizeTenantUrl(rawInput?: string | null): string {
  if (!rawInput || !rawInput.trim()) {
    return DEFAULT_API_BASE_URL;
  }

  let cleaned = rawInput.trim();

  // Strip trailing slashes and /api/v2 suffix if user pasted a full endpoint
  cleaned = cleaned.replace(/\/+$/, '').replace(/\/api\/v2\/?$/i, '');

  const isLocal = /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?/i.test(cleaned);

  if (isLocal) {
    if (!/^https?:\/\//i.test(cleaned)) {
      cleaned = `http://${cleaned}`;
    }
  } else {
    // For non-localhost, enforce https
    if (/^http:\/\//i.test(cleaned)) {
      cleaned = cleaned.replace(/^http:\/\//i, 'https://');
    } else if (!/^https:\/\//i.test(cleaned)) {
      // If it has no dots, treat as workspace slug e.g. "cust1" -> "cust1.eaconnect.io"
      if (!cleaned.includes('.')) {
        cleaned = `https://${cleaned}.eaconnect.io`;
      } else {
        cleaned = `https://${cleaned}`;
      }
    }
  }

  // Ensure path ends with /analytics if no specific path was provided
  try {
    const url = new URL(cleaned);
    if (!url.pathname || url.pathname === '/') {
      url.pathname = '/analytics';
    }
    return url.toString().replace(/\/+$/, '');
  } catch {
    return cleaned;
  }
}

/**
 * Get active API base URL.
 * Checks localStorage first, then Office roamingSettings, then default.
 */
export function getApiBaseUrl(): string {
  try {
    const stored = localStorage.getItem(TENANT_STORAGE_KEY);
    if (stored && stored.trim()) {
      return stored.trim();
    }
  } catch {
    // localStorage might be unavailable
  }

  // Check Office roamingSettings if available
  if (typeof Office !== 'undefined' && Office?.context?.roamingSettings) {
    try {
      const roaming = Office.context.roamingSettings.get(TENANT_STORAGE_KEY);
      if (typeof roaming === 'string' && roaming.trim()) {
        return roaming.trim();
      }
    } catch {
      // Ignore roaming read errors
    }
  }

  return DEFAULT_API_BASE_URL;
}

/**
 * Save active API base URL to both local storage and Office roamingSettings.
 */
export function setApiBaseUrl(rawInput: string): string {
  const normalized = normalizeTenantUrl(rawInput);

  try {
    localStorage.setItem(TENANT_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage errors
  }

  if (typeof Office !== 'undefined' && Office?.context?.roamingSettings) {
    try {
      Office.context.roamingSettings.set(TENANT_STORAGE_KEY, normalized);
      Office.context.roamingSettings.saveAsync();
    } catch {
      // Ignore roaming write errors
    }
  }

  return normalized;
}

/**
 * Reset API base URL back to default.
 */
export function resetApiBaseUrl(): string {
  try {
    localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch {
    // Ignore
  }

  if (typeof Office !== 'undefined' && Office?.context?.roamingSettings) {
    try {
      Office.context.roamingSettings.remove(TENANT_STORAGE_KEY);
      Office.context.roamingSettings.saveAsync();
    } catch {
      // Ignore
    }
  }

  return DEFAULT_API_BASE_URL;
}
